import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  FlatList,
  ActivityIndicator,
  Animated,
  LayoutAnimation,
  UIManager,
  StatusBar,
  Share,
  Alert,
  Platform,
} from 'react-native';
import {
  useNavigation,
  useRoute,
  RouteProp,
  useFocusEffect,
} from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// UI Components
import Button from '../../components/common/Button';
import TextField from '../../components/common/TextField';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import PremiumFeature from '../../components/shared/PremiumFeature';
import PageHero from '../../components/shared/PageHero';

// Types & APIs
import { RootStackParamList } from '../../types/navigation';
import {
  getWardrobeItems,
  addWardrobeItem,
  WardrobeItem,
} from '../../api/wardrobe';
import { getColourProfiles, ColourProfile } from '../../api/colours';

// Context & Hooks
import { useSubscription } from '../../hooks/useSubscription';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import { useAuth } from '../../hooks/useAuth';
import CommonHeader from '../../components/common/Header';

// Enable LayoutAnimation for smooth transitions
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const AddItemForm = memo(
  ({
    newItemImage,
    error,
    addingItem,
    onPickImage,
    onSubmit,
    onCancel,
  }: {
    newItemImage: string | null;
    error: string | null;
    addingItem: boolean;
    onPickImage: () => void;
    onSubmit: (name: string, tags: string) => void;
    onCancel: () => void;
  }) => {
    const [itemName, setItemName] = useState('');
    const [itemTags, setItemTags] = useState('');
    const { colors } = useTheme();

    const handleSubmit = useCallback(() => {
      onSubmit(itemName, itemTags);
    }, [itemName, itemTags, onSubmit]);

    const prevAddingItem = React.useRef(addingItem);
    useEffect(() => {
      if (prevAddingItem.current && !addingItem) {
        setItemName('');
        setItemTags('');
      }
      prevAddingItem.current = addingItem;
    }, [addingItem]);

    return (
      <Animated.View
        style={[
          styles.addForm,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <View style={styles.formHeader}>
          <Text style={[styles.formTitle, { color: colors.text }]}>
            Add to Your Wardrobe
          </Text>
          <TouchableOpacity onPress={onCancel} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={colors.secondary} />
          </TouchableOpacity>
        </View>

        {newItemImage ? (
          <View style={styles.uploadedImageContainer}>
            <Image
              key={newItemImage}
              source={{ uri: newItemImage }}
              style={styles.uploadedImage}
              resizeMode="cover"
            />
            <TouchableOpacity
              style={[
                styles.changeImageButton,
                { backgroundColor: colors.primary + '15' },
              ]}
              onPress={onPickImage}
            >
              <Ionicons name="camera" size={16} color={colors.primary} />
              <Text style={[styles.changeImageText, { color: colors.primary }]}>
                Change Photo
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.uploadContainer, { borderColor: colors.border }]}
            onPress={onPickImage}
            activeOpacity={0.8}
          >
            <View
              style={[
                styles.uploadIcon,
                { backgroundColor: colors.primary + '15' },
              ]}
            >
              <Ionicons name="camera" size={32} color={colors.primary} />
            </View>
            <Text style={[styles.uploadText, { color: colors.text }]}>
              Take or upload a photo
            </Text>
            <Text style={[styles.uploadSubtext, { color: colors.secondary }]}>
              We'll analyze colors and suggest matches
            </Text>
          </TouchableOpacity>
        )}

        {newItemImage && (
          <View style={styles.formFields}>
            <TextField
              label="Item Name"
              placeholder="e.g., Blue Summer Dress"
              value={itemName}
              onChangeText={setItemName}
              containerStyle={styles.formInput}
            />

            <TextField
              label="Tags (optional)"
              placeholder="casual, summer, formal"
              value={itemTags}
              onChangeText={setItemTags}
              containerStyle={styles.formInput}
            />

            {error && (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={16} color="#EF4444" />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            <Button
              title={addingItem ? 'Adding to Wardrobe...' : 'Add to Wardrobe'}
              onPress={handleSubmit}
              disabled={!newItemImage || !itemName.trim() || addingItem}
              style={styles.submitButton}
              trackClick={true}
              trackingLocation="smart-wardrobe-screen"
              trackingData={{
                button_name: 'Add to Wardrobe',
                button_type: 'add_to_wardrobe',
                screen: 'smart_wardrobe',
              }}
            />
          </View>
        )}
      </Animated.View>
    );
  }
);

const WardrobeItemCard = memo(
  ({ item, onPress }: { item: WardrobeItem; onPress: () => void }) => {
    const { colors } = useTheme();

    const getMatchColor = (match: string) => {
      switch (match) {
        case 'excellent':
          return palette.cyanLimeGreen;
        case 'good':
          return palette.brightBlue;
        case 'neutral':
          return palette.mediumGray;
        case 'poor':
          return palette.brightMatteRed;
        default:
          return palette.mediumGray;
      }
    };

    return (
      <TouchableOpacity
        style={[
          styles.itemCard,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
        onPress={onPress}
        activeOpacity={0.95}
      >
        <Image source={{ uri: item.image }} style={styles.itemImage} />

        <View style={styles.itemDetails}>
          <Text
            style={[styles.itemName, { color: colors.text }]}
            numberOfLines={1}
          >
            {item.name}
          </Text>

          <View style={styles.tagsContainer}>
            {item.tags.slice(0, 3).map((tag, index) => (
              <View
                key={index}
                style={[styles.tag, { backgroundColor: colors.primary + '15' }]}
              >
                <Text style={[styles.tagText, { color: colors.primary }]}>
                  {tag}
                </Text>
              </View>
            ))}
            {item.tags.length > 3 && (
              <Text style={[styles.moreTagsText, { color: colors.secondary }]}>
                +{item.tags.length - 3}
              </Text>
            )}
          </View>

          <View style={styles.matchContainer}>
            <Text style={[styles.matchLabel, { color: colors.secondary }]}>
              Color Match:
            </Text>
            <View
              style={[
                styles.matchIndicator,
                { backgroundColor: getMatchColor(item.paletteMatch) },
              ]}
            >
              <Text style={styles.matchIndicatorText}>{item.paletteMatch}</Text>
            </View>
          </View>
        </View>

        <TouchableOpacity style={styles.itemActions}>
          <Ionicons
            name="ellipsis-vertical"
            size={20}
            color={colors.secondary}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  }
);

type SmartWardrobeScreenRouteProp = RouteProp<
  RootStackParamList,
  'SmartWardrobe'
>;

const SmartWardrobeScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<SmartWardrobeScreenRouteProp>();
  const { colors } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { isPremium, plan, refreshSubscription } = useSubscription();

  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [colourProfiles, setColourProfiles] = useState<ColourProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemImage, setNewItemImage] = useState<string | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'match'>('date');
  const [isFetching, setIsFetching] = useState(false);

  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));

  const latestColorProfile = useMemo((): ColourProfile | null => {
    if (colourProfiles.length === 0) return null;

    const sortedProfiles = [...colourProfiles].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return dateB.getTime() - dateA.getTime();
    });

    return sortedProfiles[0];
  }, [colourProfiles]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const fetchData = async () => {
    // Prevent multiple simultaneous API calls
    if (isFetching) {
      return;
    }

    try {
      setIsFetching(true);
      setLoading(true);

      // Skip refreshSubscription on web to avoid potential issues
      if (Platform.OS !== 'web') {
        try {
          await refreshSubscription();
        } catch (err) {
          // Silent fail for subscription refresh
        }
      }

      let wardrobeItems: WardrobeItem[] = [];
      let profiles: ColourProfile[] = [];

      // Fetch wardrobe items
      try {
        wardrobeItems = await getWardrobeItems();
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          // Wardrobe fetch failed
        }
      }

      // Fetch color profiles
      try {
        profiles = await getColourProfiles();
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          // Color profiles fetch failed
        }
      }

      setWardrobeItems(wardrobeItems);
      setColourProfiles(profiles);
    } catch {
      setWardrobeItems([]);
      setColourProfiles([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();

    // Force loading to resolve after 5 seconds as fallback
    const fallbackTimer = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  useEffect(() => {
    if (route.params?.autoShowAddForm && !loading) {
      setShowAddForm(true);
      // Removed hasData state - using wardrobeItems.length directly
    }
  }, [route.params?.autoShowAddForm, loading]);

  useFocusEffect(
    useCallback(() => {
      // Only refresh if we're not already loading, not showing the form, not fetching, and don't have data yet
      if (
        !showAddForm &&
        !loading &&
        !isFetching &&
        wardrobeItems.length === 0 &&
        colourProfiles.length === 0
      ) {
        fetchData();
      }
    }, [
      showAddForm,
      loading,
      isFetching,
      wardrobeItems.length,
      colourProfiles.length,
    ])
  );

  const handleAddFirstItem = useCallback(() => {
    if (!latestColorProfile) {
      navigation.navigate('MainTabs', {
        initialTab: 'ColourAnalysis',
        fromWardrobe: true,
        showWardrobeButton: true,
      });
    } else {
      setShowAddForm(true);
    }
  }, [latestColorProfile, navigation]);

  const pickImage = useCallback(async () => {
    try {
      setError(null);

      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (status !== 'granted') {
        setError('Camera roll permissions are required to upload images.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setNewItemImage(result.assets[0].uri);
      }
    } catch {
      setError('Failed to pick image. Please try again.');
    }
  }, []);

  const addClothingItem = useCallback(
    async (itemName: string, itemTags: string) => {
      if (!newItemImage) {
        setError('Please select an image for your clothing item');
        return;
      }

      if (!itemName.trim()) {
        setError('Please enter a name for your clothing item');
        return;
      }

      try {
        setAddingItem(true);
        setError(null);

        const tagsArray = itemTags
          .split(',')
          .map((tag: string) => tag.trim().toLowerCase())
          .filter((tag: string) => tag.length > 0);

        const newItem = await addWardrobeItem(
          newItemImage,
          itemName,
          tagsArray
        );

        setWardrobeItems([newItem, ...wardrobeItems]);
        setNewItemImage(null);
        setShowAddForm(false);
        // Removed hasData state - using wardrobeItems.length directly

        await fetchData();
      } catch {
        setError('Failed to add clothing item. Please try again.');
      } finally {
        setAddingItem(false);
      }
    },
    [newItemImage, wardrobeItems, fetchData]
  );

  const handleShareWardrobe = useCallback(async () => {
    try {
      const message = `Check out my smart wardrobe with ${wardrobeItems.length} items! I'm using ColorSense to organize my style.`;
      await Share.share({
        message,
        url: 'https://mycoloursense.com',
      });
    } catch {
      // Share failed
    }
  }, [wardrobeItems.length]);

  const handleItemPress = useCallback((item: WardrobeItem) => {
    // Handle item press
  }, []);

  const renderHeader = () => {
    const rightActions = (
      <View style={styles.headerActions}>
        {wardrobeItems.length > 0 && (
          <TouchableOpacity
            style={styles.shareButton}
            onPress={handleShareWardrobe}
            activeOpacity={0.8}
          >
            <Ionicons name="share-outline" size={20} color="white" />
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={styles.addItemButton}
          onPress={() => setShowAddForm(!showAddForm)}
          activeOpacity={0.8}
        >
          <Ionicons name="add" size={24} color="white" />
        </TouchableOpacity>
      </View>
    );

    return (
      <CommonHeader
        title="Smart Wardrobe"
        subtitle={
          wardrobeItems.length > 0
            ? `${wardrobeItems.length} items • AI-powered suggestions`
            : 'Organize your style with AI'
        }
        rightActions={rightActions}
        gradientColors={[palette.softBlue, palette.darkModerateViolet]}
        animatedStyle={{
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        }}
      />
    );
  };

  const renderColorPalette = () => (
    <Animated.View
      style={[
        styles.paletteSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View style={styles.paletteHeader}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Your Color Palette
        </Text>
        {latestColorProfile && (
          <Text style={[styles.paletteName, { color: colors.primary }]}>
            {latestColorProfile.name}
          </Text>
        )}
      </View>

      {latestColorProfile ? (
        <View
          style={[styles.paletteContainer, { backgroundColor: colors.card }]}
        >
          {latestColorProfile.palette.colours.map(
            (color: any, index: number) => (
              <View
                key={index}
                style={[
                  styles.paletteColor,
                  { backgroundColor: color.hex },
                  { borderColor: colors.border },
                ]}
              />
            )
          )}
        </View>
      ) : (
        <TouchableOpacity
          style={[styles.noPaletteContainer, { backgroundColor: colors.card }]}
          onPress={() =>
            navigation.navigate('MainTabs', { initialTab: 'ColourAnalysis' })
          }
        >
          <Ionicons name="color-palette" size={32} color={colors.primary} />
          <Text style={[styles.noPaletteText, { color: colors.text }]}>
            Create your color profile
          </Text>
          <Text style={[styles.noPaletteSubtext, { color: colors.secondary }]}>
            Get personalized wardrobe suggestions
          </Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );

  const renderEmptyState = () => (
    <Animated.View
      style={[
        styles.emptyStateContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View style={styles.emptyStateContent}>
        <View
          style={[
            styles.emptyStateIcon,
            { backgroundColor: colors.primary + '15' },
          ]}
        >
          <Ionicons name="shirt" size={48} color={colors.primary} />
        </View>

        <Text style={[styles.emptyStateTitle, { color: colors.text }]}>
          Start Your Smart Wardrobe
        </Text>

        <Text style={[styles.emptyStateMessage, { color: colors.secondary }]}>
          Upload your clothes and get AI-powered outfit suggestions that match
          your unique colour palette.
        </Text>

        <View style={styles.emptyStateStats}>
          <View style={styles.statItem}>
            <Text style={[styles.statNumber, { color: colors.primary }]}>
              ∞
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Outfit Combinations
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text style={[styles.statNumber, { color: colors.primary }]}>
              AI
            </Text>
            <Text style={[styles.statLabel, { color: colors.secondary }]}>
              Smart Matching
            </Text>
          </View>
        </View>

        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: colors.primary }]}
          onPress={handleAddFirstItem}
          activeOpacity={0.8}
        >
          <Ionicons name="add" size={20} color="white" />
          <Text style={styles.createButtonText}>Add Your First Item</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={colors.primary} />
      <Text style={[styles.loadingText, { color: colors.secondary }]}>
        Loading your wardrobe...
      </Text>
    </View>
  );

  const renderMainContent = () => (
    <Animated.View
      style={[
        styles.mainContent,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {renderColorPalette()}

      <View style={styles.wardrobeSection}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Your Wardrobe ({wardrobeItems.length})
          </Text>

          {wardrobeItems.length > 0 && (
            <TouchableOpacity
              style={styles.viewAllButton}
              onPress={() => {
                /* TODO: Navigate to full wardrobe view */
              }}
            >
              <Text style={[styles.viewAllText, { color: colors.primary }]}>
                View All
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {showAddForm && (
          <AddItemForm
            newItemImage={newItemImage}
            error={error}
            addingItem={addingItem}
            onPickImage={pickImage}
            onSubmit={addClothingItem}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {wardrobeItems.length > 0 ? (
          <FlatList
            data={wardrobeItems.slice(0, 5)} // Show first 5 items for performance
            renderItem={({ item }) => (
              <WardrobeItemCard
                item={item}
                onPress={() => handleItemPress(item)}
              />
            )}
            keyExtractor={(item) => item.id}
            scrollEnabled={false}
            style={styles.itemsList}
            showsVerticalScrollIndicator={false}
          />
        ) : (
          <View style={styles.noItemsContainer}>
            <Ionicons name="shirt-outline" size={48} color={colors.secondary} />
            <Text style={[styles.noItemsText, { color: colors.secondary }]}>
              No items yet
            </Text>
          </View>
        )}
      </View>
    </Animated.View>
  );

  // Force re-render if we have data but still loading
  if (loading && (wardrobeItems.length > 0 || colourProfiles.length > 0)) {
    setLoading(false);
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <StatusBar barStyle="light-content" backgroundColor="#667eea" />
        {renderLoading()}
      </View>
    );
  }

  // Always show loading first if we're in loading state
  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <StatusBar barStyle="light-content" backgroundColor="#667eea" />
        {renderLoading()}
      </View>
    );
  }

  // Show empty state if no data and not loading
  if (!wardrobeItems.length) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <StatusBar barStyle="light-content" backgroundColor="#667eea" />
        {renderHeader()}
        {renderEmptyState()}
      </View>
    );
  }

  // Show main content
  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      {renderHeader()}

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <PageHero
          title="Smart Wardrobe"
          subtitle="Your digital closet. Add items, match to your palette, and plan outfits."
        />
        {renderMainContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },

  shareButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },

  addItemButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },

  scrollView: {
    flex: 1,
  },

  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },

  mainContent: {
    gap: 16,
  },

  paletteSection: {
    marginBottom: 8,
  },

  paletteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },

  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
  },

  paletteName: {
    fontSize: 14,
    fontWeight: '600',
  },

  paletteContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    gap: 12,
  },

  paletteColor: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
  },

  noPaletteContainer: {
    alignItems: 'center',
    padding: 24,
    borderRadius: 16,
    gap: 8,
  },

  noPaletteText: {
    fontSize: 16,
    fontWeight: '600',
  },

  noPaletteSubtext: {
    fontSize: 14,
    opacity: 0.7,
  },

  wardrobeSection: {
    gap: 16,
  },

  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  viewAllButton: {
    padding: 8,
  },

  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
  },

  addForm: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },

  formTitle: {
    fontSize: 16,
    fontWeight: '700',
  },

  closeButton: {
    padding: 4,
  },

  uploadContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    marginBottom: 16,
  },

  uploadIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },

  uploadText: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },

  uploadSubtext: {
    fontSize: 12,
    opacity: 0.7,
    textAlign: 'center',
  },

  uploadedImageContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },

  uploadedImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    marginBottom: 12,
  },

  changeImageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    gap: 6,
  },

  changeImageText: {
    fontSize: 12,
    fontWeight: '600',
  },

  formFields: {
    gap: 16,
  },

  formInput: {
    marginBottom: 0,
  },

  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: palette.lightRed,
    borderRadius: 8,
    gap: 8,
  },

  errorText: {
    fontSize: 12,
    color: palette.brightMatteRed,
    flex: 1,
  },

  submitButton: {
    marginTop: 8,
  },

  itemsList: {
    gap: 12,
  },

  itemCard: {
    flexDirection: 'row',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 2,
  },

  itemImage: {
    width: 80,
    height: 80,
    borderRadius: 12,
    backgroundColor: palette.veryLightGray,
  },

  itemDetails: {
    flex: 1,
    marginLeft: 12,
    justifyContent: 'space-between',
  },

  itemName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
  },

  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 4,
  },

  tag: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
  },

  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },

  moreTagsText: {
    fontSize: 12,
    opacity: 0.6,
    alignSelf: 'center',
  },

  matchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },

  matchLabel: {
    fontSize: 12,
    opacity: 0.7,
  },

  matchIndicator: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },

  matchIndicatorText: {
    fontSize: 12,
    color: palette.white,
    fontWeight: '600',
  },

  itemActions: {
    padding: 4,
    alignSelf: 'flex-start',
  },

  noItemsContainer: {
    alignItems: 'center',
    padding: 40,
    gap: 12,
  },

  noItemsText: {
    fontSize: 16,
    opacity: 0.7,
  },

  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },

  emptyStateContent: {
    alignItems: 'center',
    maxWidth: 320,
  },

  emptyStateIcon: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },

  emptyStateTitle: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },

  emptyStateMessage: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    opacity: 0.7,
  },

  emptyStateStats: {
    flexDirection: 'row',
    gap: 32,
    marginBottom: 32,
  },

  statItem: {
    alignItems: 'center',
  },

  statNumber: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },

  statLabel: {
    fontSize: 12,
    opacity: 0.7,
    textAlign: 'center',
  },

  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 16,
    gap: 8,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },

  createButtonText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },

  loadingText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
    opacity: 0.7,
  },
});

// Wrap in PremiumFeature for subscription checks
const SmartWardrobeScreenWrapper = () => {
  return (
    <PremiumFeature>
      <SmartWardrobeScreen />
    </PremiumFeature>
  );
};

export default SmartWardrobeScreenWrapper;
